import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Eye, Menu, X, Mic } from "lucide-react";
import ModeToggle from "../ui/ModeToggle";

export default function Header({ variant = "marketing", onVoiceClick }) {
  const loc = useLocation();
  const isApp = variant === "app" || loc.pathname.startsWith("/app");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="app-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-ink-base/80 backdrop-blur-xl border-b border-ink-border-subtle/80"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 h-16 flex items-center justify-between gap-6">
        <Link
          to="/"
          className="flex items-center gap-2.5 group shrink-0"
          data-testid="brand-link"
        >
          <div className="relative w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-brand/20 to-brand/5 border border-brand/30 group-hover:border-brand/60 transition-colors">
            <Eye size={15} className="text-brand" strokeWidth={2} />
          </div>
          <div className="leading-none">
            <span className="font-bold text-white text-[16px] tracking-tight">
              N.E.T.R.A<span className="text-brand font-semibold text-[15px] ml-0.5">.ai</span>
            </span>
            <div className="text-[9px] text-zinc-500 tracking-wide mt-0.5 leading-none">
              A Horizon Flare India Project
            </div>
          </div>
        </Link>

        {/* Desktop center pill nav */}
        <nav
          className="hidden md:flex items-center gap-1 mx-auto rounded-full border border-ink-border-subtle bg-ink-surface/60 backdrop-blur p-1"
          data-testid="primary-nav"
        >
          {isApp ? (
            // Workspace navigation
            <>
              <Link
                to="/"
                className="px-4 py-1.5 text-sm text-zinc-400 hover:text-white hover:bg-ink-soft rounded-full transition-all"
              >
                Home
              </Link>
              {[
                { href: "#analyze", label: "Analyze", id: "analyze" },
                { href: "#history", label: "History", id: "history" },
              ].map((it) => (
                <a
                  key={it.id}
                  href={it.href}
                  onClick={(e) => {
                    e.preventDefault();
                    if (it.id === 'analyze') {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else if (it.id === 'history') {
                      document.querySelector('[data-testid="history-panel"]')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  data-testid={`nav-${it.id}`}
                  className="px-4 py-1.5 text-sm text-zinc-400 hover:text-white hover:bg-ink-soft rounded-full transition-all"
                >
                  {it.label}
                </a>
              ))}
            </>
          ) : (
            // Home page navigation
            [
              { href: "#why", label: "Why N.E.T.R.A", id: "why" },
              { href: "#features", label: "Features", id: "features" },
              { href: "#how", label: "How it works", id: "how" },
            ].map((it) => (
              <a
                key={it.id}
                href={it.href}
                data-testid={`nav-${it.id}`}
                className="px-4 py-1.5 text-sm text-zinc-400 hover:text-white hover:bg-ink-soft rounded-full transition-all"
              >
                {it.label}
              </a>
            ))
          )}
        </nav>

        {/* Right CTA / back link */}
        <div className="flex items-center gap-2 shrink-0">
          {isApp ? (
            <button
              onClick={onVoiceClick}
              className="inline-flex items-center gap-2 bg-zinc-800/60 hover:bg-zinc-700/60 text-white px-4 py-2 rounded-full transition-all duration-300 border border-zinc-700/50 text-sm"
            >
              <Mic size={14} className="text-signal-info" />
              <span className="hidden sm:inline">Voice Mode</span>
            </button>
          ) : (
            <ModeToggle />
          )}
          <button
            className="md:hidden text-zinc-400 hover:text-white p-1.5 -mr-1.5"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && !isApp && (
        <div className="md:hidden border-t border-ink-border-subtle bg-ink-base/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-1">
            {[
              { href: "#why", label: "Why N.E.T.R.A" },
              { href: "#how", label: "How it works" },
              { href: "#features", label: "Features" },
            ].map((it) => (
              <a
                key={it.href}
                href={it.href}
                onClick={() => setMobileOpen(false)}
                className="text-zinc-300 hover:text-white px-3 py-2.5 rounded-lg hover:bg-ink-soft transition-colors"
              >
                {it.label}
              </a>
            ))}

          </div>
        </div>
      )}
    </header>
  );
}
